/**
 * MyTenants — lista tenantów zalogowanego usera + switch cross-subdomain (F7b T7b.8).
 *
 * Źródło danych: `GET /api/my/tenants` (tenant-scoped, D21=A). Zwraca listę z
 * flagą `isCurrent` dla bieżącego tenanta (wiersz disabled + „(obecny)" badge).
 *
 * Switch: klik „Przełącz" → `buildTenantLoginUrl({slug, email})` — cross-subdomain
 * redirect. Spójne z TenantSelect (T7b.3) — brak session sharing między subdomenami,
 * user ponownie loguje się w innym tenancie (access token scope = tenantId).
 *
 * Edge cases:
 *  - 0 tenantów (nie powinno się zdarzyć — user jest zalogowany, więc ma ≥1) → info.
 *  - 1 tenant → render listy (bez redirect, w przeciwieństwie do TenantSelect który
 *    robi auto-redirect w public scope — tutaj user jest już w swoim tenancie).
 *  - Error → alert + retry button.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Spinner } from '@/components/Spinner';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { api } from '@/lib/api';
import { buildTenantLoginUrl } from '@/lib/tenant-url';

interface MyTenantEntry {
  tenantId: string;
  slug: string;
  name: string;
  isCurrent: boolean;
}

interface MyTenantsResponse {
  tenants: MyTenantEntry[];
}

type State = { kind: 'loading' } | { kind: 'ok'; tenants: MyTenantEntry[] } | { kind: 'error' };

export function MyTenants() {
  const { t } = useTranslation();
  const { me } = useTenantAuth();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    (async () => {
      try {
        const res = await api.get<MyTenantsResponse>('/my/tenants');
        if (cancelled) return;
        setState({ kind: 'ok', tenants: res.data.tenants ?? [] });
      } catch {
        if (cancelled) return;
        setState({ kind: 'error' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const onSwitch = (slug: string) => {
    const email = me?.user.email;
    window.location.href = buildTenantLoginUrl({ slug, ...(email ? { email } : {}) });
  };

  return (
    <main className="min-h-full bg-bg-secondary p-6" data-testid="my-tenants">
      <div className="mx-auto max-w-4xl space-y-5">
        <header>
          <h1 className="text-2xl font-bold text-text-primary">{t('tenant.myTenants.title')}</h1>
          <p className="mt-1 text-sm text-text-tertiary">{t('tenant.myTenants.subtitle')}</p>
        </header>

        {state.kind === 'loading' && (
          <div className="flex items-center justify-center py-10" role="status">
            <Spinner />
          </div>
        )}

        {state.kind === 'error' && (
          <div className="space-y-3">
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
            >
              {t('tenant.myTenants.errors.generic')}
            </div>
            <Button variant="ghost" onClick={() => setAttempt((n) => n + 1)}>
              {t('tenant.myTenants.retry')}
            </Button>
          </div>
        )}

        {state.kind === 'ok' && state.tenants.length === 0 && (
          <Card padding="md" data-testid="my-tenants-empty">
            <p className="text-sm text-text-tertiary">{t('tenant.myTenants.empty')}</p>
          </Card>
        )}

        {state.kind === 'ok' && state.tenants.length === 1 && (
          <div
            role="status"
            className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
            data-testid="my-tenants-single"
          >
            {t('tenant.myTenants.single')}
          </div>
        )}

        {state.kind === 'ok' && state.tenants.length > 0 && (
          <ul className="space-y-3" data-testid="my-tenants-list">
            {state.tenants.map((tenant) => (
              <li key={tenant.tenantId}>
                <Card padding="md">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-text-primary">{tenant.name}</span>
                        {tenant.isCurrent && (
                          <Badge color="accent">{t('tenant.myTenants.currentBadge')}</Badge>
                        )}
                      </div>
                      <div className="mt-0.5 text-xs text-text-tertiary">{tenant.slug}</div>
                    </div>
                    <div>
                      <Button
                        variant={tenant.isCurrent ? 'ghost' : 'secondary'}
                        size="sm"
                        disabled={tenant.isCurrent}
                        onClick={() => onSwitch(tenant.slug)}
                        data-testid={`switch-${tenant.slug}`}
                      >
                        {tenant.isCurrent
                          ? t('tenant.myTenants.currentCta')
                          : t('tenant.myTenants.switchCta')}
                      </Button>
                    </div>
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </div>
    </main>
  );
}
